import React, { useState, useEffect, useRef } from 'react';
import { useFeedbackMutation, type FeedbackPayload } from '../hooks/useFeedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchContext?: Record<string, unknown> | undefined;
  /**
   * Pre-captured screenshot data URL from FeedbackButton. The capture
   * runs before the modal mounts so the modal isn't in the image.
   * Null means capture failed or wasn't attempted; the modal hides the
   * preview UI gracefully in that case.
   */
  screenshotDataUrl?: string | null;
  /**
   * True when FeedbackButton intentionally skipped capture because the
   * current page is a draft surface (spec §5.10 — never let a customer's
   * proprietary draft content reach the shared feedback-screenshot bucket).
   * Distinct from a plain capture failure so the modal can tell the user
   * *why* there's no screenshot instead of staying silent about it.
   */
  screenshotSuppressed?: boolean;
}

const CATEGORIES: { value: FeedbackPayload['category']; label: string; icon: string }[] = [
  { value: 'bug', label: 'Bug Report', icon: 'M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { value: 'feature', label: 'Feature Request', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { value: 'general', label: 'General', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
  { value: 'data_quality', label: 'Data Quality', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  searchContext,
  screenshotDataUrl,
  screenshotSuppressed,
}) => {
  const [category, setCategory] = useState<FeedbackPayload['category']>('general');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const feedbackMutation = useFeedbackMutation();

  useEffect(() => {
    if (isOpen) {
      setCategory('general');
      setMessage('');
      setSubmitted(false);
      // Default-on per UX spec — screenshot is auto-attached unless user
      // unticks. Reset every modal open so a prior un-tick doesn't leak
      // into the next submission.
      setIncludeScreenshot(true);
      feedbackMutation.reset();
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (message.trim().length < 10) return;

    const payload: FeedbackPayload = {
      category,
      message: message.trim(),
      // Backend + DB column cap this at 500; a keyword-search URL (kwConfig) runs
      // longer and was rejected with 422. The full query rides in search_context.
      page_context: (window.location.pathname + window.location.search).slice(0, 500),
      search_context: searchContext,
    };
    if (includeScreenshot && screenshotDataUrl) {
      payload.screenshot_data_url = screenshotDataUrl;
    }

    feedbackMutation.mutate(payload, {
      onSuccess: () => {
        setSubmitted(true);
        setTimeout(onClose, 2000);
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-feedback-skip
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Send Feedback</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {submitted ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-3">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-slate-900">Thank you!</p>
              <p className="text-sm text-slate-500 mt-1">Your feedback has been submitted.</p>
            </div>
          ) : (
            <>
              {/* Category selector */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Category</label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                        category === cat.value
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={cat.icon} />
                      </svg>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Message
                  <span className="text-slate-400 font-normal ml-1">(min 10 characters)</span>
                </label>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe the issue, suggestion, or feedback..."
                  rows={4}
                  maxLength={5000}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 resize-none"
                />
                <div className="flex justify-between mt-1">
                  {message.length > 0 && message.trim().length < 10 && (
                    <span className="text-xs text-amber-600">
                      {10 - message.trim().length} more characters needed
                    </span>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">{message.length}/5000</span>
                </div>
              </div>

              {/* Screenshot suppressed on a draft surface — tell the user why
                  there's nothing to preview instead of staying silent. */}
              {screenshotSuppressed && (
                <p className="text-xs text-slate-400">
                  Screenshot not attached on draft pages.
                </p>
              )}

              {/* Screenshot — preview + opt-out */}
              {screenshotDataUrl && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                    <span>Screenshot</span>
                    <label className="flex items-center gap-2 text-xs font-normal text-slate-500 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeScreenshot}
                        onChange={(e) => setIncludeScreenshot(e.target.checked)}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
                      />
                      Include with submission
                    </label>
                  </label>
                  <div
                    className={`relative border rounded-lg overflow-hidden transition-opacity ${
                      includeScreenshot ? 'border-slate-200 opacity-100' : 'border-slate-100 opacity-40'
                    }`}
                  >
                    <img
                      src={screenshotDataUrl}
                      alt="Screenshot preview"
                      className="w-full max-h-48 object-contain bg-slate-50"
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {feedbackMutation.isError && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                  Failed to submit feedback. Please try again.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={message.trim().length < 10 || feedbackMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {feedbackMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
